import { cn } from "@/lib/cn"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { ComponentPropsWithRef } from "react"

/* -------------------------------------------------------------------------------------------------
 * utils
 * -----------------------------------------------------------------------------------------------*/

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-btn font-bold tracking-wide disabled:pointer-events-none disabled:opacity-40",
  {
    variants: {
      variant: {
        default:
          "border-btn border-btn-primary-border bg-btn-primary text-paragraph-m text-btn-primary-text disabled:border-btn-primary-disabled-border disabled:bg-btn-primary-disabled disabled:text-btn-primary-disabled-text",
        secondary:
          "border-btn border-btn-secondary-border bg-btn-secondary text-paragraph-m  text-btn-secondary-text",
        outline:
          "border-px border-btn-outlined-border bg-btn-outlined text-paragraph-m text-btn-outlined-text",
        rounded:
          "rounded-full border-btn-primary-border bg-btn-primary text-paragraph-m text-btn-primary-text disabled:border-btn-primary-disabled disabled:bg-btn-primary-disabled",
        roundedOutline:
          "rounded-full border-px border-btn-outlined-border bg-btn-outlined text-paragraph-m text-btn-outlined-text",
        ghost: "border-none bg-transparent",
      },
      size: {
        default: "h-9 px-4 py-[.375rem]",
        dialogSubmit:
          "w-full max-w-[20.5rem] px-[1.9375rem] py-[0.5625rem] md:w-unset md:min-w-[unset]",
        clear: "h-[unset] p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

/* -------------------------------------------------------------------------------------------------
 * component
 * -----------------------------------------------------------------------------------------------*/

type ButtonProps = ComponentPropsWithRef<"button"> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean }

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  type = "button",
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      type={type}
      {...props}
    />
  )
}

export { Button, buttonVariants }
export type { ButtonProps }
